import { z } from 'zod'
import { ENTITLEMENT_MODULE_IDS } from './feature-toggle.ts'

/**
 * Controle de acesso por workspace (SPEC-20260814-1444).
 *
 * O plano (entitlement) define o que o workspace COMPROU; o perfil define o
 * que cada usuario PODE dentro disso. Tres niveis de permissao:
 *
 *  - MODULO   (`work-items.access`)      — ve o modulo no rail e abre as telas
 *  - FUNCIONALIDADE (menus)              — agrupador dentro do modulo
 *  - ACAO     (`work-items.item.create`) — consultar, incluir, salvar, botoes
 *
 * A resolucao e SEMPRE do servidor (padrao bundle, como toggles e i18n).
 * Regras de adocao decididas pelo usuario:
 *  - dono do workspace SEMPRE pode tudo (anti-lockout);
 *  - perfil Administrador (is_system) concede tudo;
 *  - workspace sem NENHUM vinculo de perfil = tudo liberado ("tudo, ate
 *    configurarem");
 *  - com vinculos, usuario sem perfil = somente consulta (acoes kind 'view').
 */

// --- Catalogo -----------------------------------------------------------

/** `view` libera no modo somente-consulta; `mutate` exige perfil. */
export type PermissionKind = 'view' | 'mutate'

export type PermissionAction = {
  /** Chave completa `modulo.funcionalidade.acao`. */
  key: string
  label: string
  kind: PermissionKind
}

export type PermissionFeature = {
  /** `modulo.funcionalidade` — o "menu" dentro do modulo. */
  key: string
  label: string
  actions: readonly PermissionAction[]
}

export type PermissionModule = {
  /** Id do modulo do shell (mesmo de ENTITLEMENT_MODULE_IDS) ou 'admin'. */
  moduleId: string
  label: string
  /** Chave da permissao de acesso ao modulo: `<moduleId>.access`. */
  accessKey: string
  features: readonly PermissionFeature[]
}

/** `work-items` -> `work-items.access`. */
export function moduleAccessKey(moduleId: string): string {
  return `${moduleId}.access`
}

const acao = (feature: string, key: string, label: string, kind: PermissionKind): PermissionAction => ({
  key: `${feature}.${key}`,
  label,
  kind,
})

/**
 * Catalogo canonico de permissoes.
 *
 * Fundacao (SPEC-20260814-1444): acesso de modulo para TODOS os modulos do
 * shell (+ admin) e funcionalidades/acoes detalhadas nos dois modulos PILOTO
 * (Itens de Trabalho e Demandas). Os demais modulos ganham acoes em SPECs
 * seguintes — acrescente aqui e semeie o guard junto.
 */
export const PERMISSION_CATALOG: readonly PermissionModule[] = [
  {
    moduleId: 'work-items',
    label: 'Itens de Trabalho',
    accessKey: moduleAccessKey('work-items'),
    features: [
      {
        key: 'work-items.item',
        label: 'Item de Trabalho',
        actions: [
          acao('work-items.item', 'view', 'Consultar', 'view'),
          acao('work-items.item', 'create', 'Incluir', 'mutate'),
          acao('work-items.item', 'update', 'Salvar alterações', 'mutate'),
          acao('work-items.item', 'move-status', 'Mover de status', 'mutate'),
          acao('work-items.item', 'archive', 'Arquivar', 'mutate'),
          // apaga de vez, sem volta (SPEC-20261008-1649-excluir-e-arquivar-item)
          acao('work-items.item', 'delete', 'Excluir definitivamente', 'mutate'),
        ],
      },
    ],
  },
  // Acesso de modulo para o resto do shell (sem acoes detalhadas ainda)
  ...ENTITLEMENT_MODULE_IDS.filter((id) => id !== 'work-items').map((moduleId) => ({
    moduleId,
    label: moduleId,
    accessKey: moduleAccessKey(moduleId),
    features: [] as readonly PermissionFeature[],
  })),
  {
    moduleId: 'admin',
    label: 'Administração',
    accessKey: moduleAccessKey('admin'),
    features: [],
  },
]

/** Todas as chaves validas do catalogo (acessos de modulo + acoes). */
export function allPermissionKeys(): string[] {
  return PERMISSION_CATALOG.flatMap((m) => [m.accessKey, ...m.features.flatMap((f) => f.actions.map((a) => a.key))])
}

/** Chaves liberadas no modo somente-consulta: acessos de modulo + acoes view. */
export function viewOnlyPermissionKeys(): Set<string> {
  return new Set(
    PERMISSION_CATALOG.flatMap((m) => [
      m.accessKey,
      ...m.features.flatMap((f) => f.actions.filter((a) => a.kind === 'view').map((a) => a.key)),
    ]),
  )
}

// --- Entidades ----------------------------------------------------------

export const accessProfileSchema = z.object({
  id: z.string().uuid(),
  workspaceId: z.string().uuid(),
  name: z.string().min(1).max(120),
  description: z.string().nullable(),
  /** Administrador semeado: concede TUDO e nao pode ser excluido/renomeado. */
  isSystem: z.boolean(),
})
export type AccessProfile = z.infer<typeof accessProfileSchema>

export const accessGroupSchema = z.object({
  id: z.string().uuid(),
  workspaceId: z.string().uuid(),
  name: z.string().min(1).max(120),
  description: z.string().nullable(),
})
export type AccessGroup = z.infer<typeof accessGroupSchema>

// --- Resolucao ----------------------------------------------------------

export type AccessResolutionContext = {
  /** O usuario e dono (role owner) do workspace da sessao. */
  isOwner: boolean
  /** Existe QUALQUER vinculo de perfil no workspace (usuario ou grupo)? */
  workspaceHasAssignments: boolean
  /** O usuario tem algum perfil is_system (Administrador)? */
  hasSystemProfile: boolean
  /** Uniao das chaves dos perfis do usuario (diretos + via grupos). */
  grantedKeys: ReadonlySet<string>
}

/**
 * O usuario pode `key`?
 * dono/Administrador → tudo; workspace sem vinculos → tudo; senao perfis;
 * sem a chave no perfil, vale o modo somente-consulta (kind view).
 */
export function resolvePermission(key: string, context: AccessResolutionContext, viewOnly: ReadonlySet<string>): boolean {
  if (context.isOwner || context.hasSystemProfile) return true
  if (!context.workspaceHasAssignments) return true
  if (context.grantedKeys.has(key)) return true
  return viewOnly.has(key)
}

/** Mapa `chave -> boolean` para o bundle do usuario. */
export function resolvePermissionBundle(context: AccessResolutionContext): Record<string, boolean> {
  const viewOnly = viewOnlyPermissionKeys()
  const bundle: Record<string, boolean> = {}
  for (const key of allPermissionKeys()) {
    bundle[key] = resolvePermission(key, context, viewOnly)
  }
  return bundle
}
